import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';
import DealerAd from '../..';
import cn from 'classnames';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton/IconButton';
import Close16 from '@wix/da-ds/pkg/Icons/16x16/Close/Close';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { yieldToMainAsync } from '@wix/da-hooks/pkg/yieldToMain';
import isEmpty from 'lodash/isEmpty';
import { useCloseTracking } from '../../useCloseTracking';

import {
  ThemeOptions,
  ThemeOverride,
} from '@wix/da-react-context/pkg/ThemeContext';
import s from './Modal.scss';

export interface Props {
  realEstateId: string;
}

const ModalAdUnit: React.FC<Props> = ({ realEstateId }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [passedOffer, setPassedOffer] = useState<any>(undefined);
  const isMobile = useContext(MobileContext);
  const isMounted = useIsMounted();
  const isVideo = useRef(false);

  const iframeCommandHandler = useCallback((command, data) => {
    switch (command) {
      case 'show-modal':
        if (data && data.asset.creative.videobanner) {
          // If there are any non-empty keys inside videobanner,
          // we can treat this as having video data
          const { videobanner } = data.asset.creative;
          if (
            Object.keys(videobanner).length > 0 &&
            Object.entries(videobanner)
              .map(([k, v]) => Object.keys(v || {}).length)
              .reduce((acc, i) => acc + i, 0) > 0
          ) {
            isVideo.current = true;
          }
        }
        setPassedOffer(data);
        setIsVisible(true);
        break;

      default:
        break;
    }
  }, []);

  const closeHandler = useCloseTracking(passedOffer, realEstateId, {
    iframeCommandHandler,
    onCloseStart: async () => {
      await yieldToMainAsync();
      if (dialogRef?.current) {
        dialogRef.current.close();
      }
      setIsVisible(false);
      setPassedOffer(undefined);
    },
  });

  useEffect(() => {
    const dialog = dialogRef.current;

    if (!dialog) {
      return;
    }

    // Handle outside clicks
    const clickOutside = (event: MouseEvent) => {
      if (!dialog) {
        return;
      }

      const rect = dialog.getBoundingClientRect();
      if (event.clientY < rect.top || event.clientY > rect.bottom) {
        return closeHandler();
      }
      if (event.clientX < rect.left || event.clientX > rect.right) {
        return closeHandler();
      }
    };

    // Attach listener once per effect run
    dialog.addEventListener('click', clickOutside);

    // Only attempt to show when we actually want it visible
    if (isVisible && passedOffer && document.contains(dialog) && !dialog.open) {
      let width, height;

      // Mobile either is given dimensions or we scale down to 300x400
      if (isMobile) {
        width = passedOffer.asset.creative.modal.mobile.width ?? '300';
        height = passedOffer.asset.creative.modal.mobile.height ?? '400';
      } else {
        width = passedOffer.asset.creative.modal.desktop.width;
        height = passedOffer.asset.creative.modal.desktop.height;
      }

      const calcWidth = `${width}px`;
      const calcHeight = `${height}px`;

      dialog.style.minWidth = calcWidth;
      dialog.style.width = calcWidth;
      dialog.style.minHeight = calcHeight;
      dialog.style.height = calcHeight;

      // Video is width only
      if (isVideo.current) {
        dialog.style.minHeight = 'auto';
        dialog.style.height = 'auto';
      }

      // Nothing to display
      const noDesktopModal =
        !isMobile && isEmpty(passedOffer.asset.creative.modal.desktop);
      const noMobileModal =
        isMobile &&
        isEmpty(passedOffer.asset.creative.modal.mobile.iframeContent);

      const shouldShow = !(noDesktopModal || noMobileModal);

      if (shouldShow) {
        try {
          dialog.showModal();
        } catch (err: any) {
          // In case of rare race conditions, ignore only the invalid state error
          if (err?.name !== 'InvalidStateError') {
            throw err;
          }
        }
      }
    }

    return () => {
      dialog.removeEventListener('click', clickOutside);
    };
  }, [isVisible, passedOffer, isMobile, closeHandler]);

  if (!isMounted) {
    return null;
  }

  return (
    <ErrorBoundary debugComponent="ModalAdUnit">
      <div className={cn(s['root'], isVisible && s['visible'])}>
        <dialog
          className={cn(s['dialog'], isVideo.current && s['video'])}
          ref={dialogRef}
        >
          <DealerAd
            disableLazy
            className={s['unit']}
            realEstateId={realEstateId}
            extraParams={
              process.env.NODE_ENV === 'development'
                ? {
                    forcefixture: 'mobilemodal',
                  }
                : {}
            }
          />
          {!isVideo.current && (
            <ThemeOverride theme={ThemeOptions.DARK}>
              <IconButton
                icon={Close16}
                size={'medium'}
                aria-label={t('common.close')}
                className={cn(s['close'])}
                onClick={closeHandler}
              />
            </ThemeOverride>
          )}
          {isVideo.current && (
            <IconButton
              icon={Close16}
              size={'medium'}
              aria-label={t('common.close')}
              className={cn(s['close'], s['video'])}
              onClick={closeHandler}
            />
          )}
        </dialog>
      </div>
    </ErrorBoundary>
  );
};
ModalAdUnit.displayName = 'ModalAdUnit';

export default ModalAdUnit;
